import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';

// `source` styr vilken endpoint som används:
//   'django'  → /api/admin/logs/<key>      (api/bot/stats — proxas till Django)
//   'mcp'     → /api/admin/mcp/logs/<key>  (telemetry/audit — laser jsonl direkt)
const SERVICES = [
  { key: 'api', label: 'API', source: 'django' },
  { key: 'bot', label: 'Bot', source: 'django' },
  { key: 'stats', label: 'Stats', source: 'django' },
  { key: 'telemetry', label: 'MCP telemetry', source: 'mcp' },
  { key: 'audit', label: 'MCP audit', source: 'mcp' },
];

const LINE_OPTIONS = [200, 500, 1000, 2000];

/**
 * Loggar-flik i adminpanelen.
 *
 * Visar senaste raderna från en vald service-logg (api, bot, stats).
 * Fetchar `/api/admin/logs/<service>?lines=N` och renderar rå text i
 * en scrollbar pre-tagg. Antal rader styrs via dropdown.
 */
export default function AdminLogs({ hideTitle = false } = {}) {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [service, setService] = useState('api');
  const [lines, setLines] = useState(500);
  const [logDate, setLogDate] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const preRef = useRef(null);

  const activeService = SERVICES.find((s) => s.key === service) || SERVICES[0];

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ lines: String(lines) });
      if (activeService.source === 'mcp' && logDate) params.set('date', logDate);
      const path = activeService.source === 'mcp'
        ? `/api/admin/mcp/logs/${service}`
        : `/api/admin/logs/${service}`;
      const r = await fetch(`${API_URL}${path}?${params.toString()}`, {
        headers: getAuthHeaders(),
      });
      if (r.ok) {
        setData(await r.json());
      } else {
        const body = await r.json().catch(() => ({}));
        setError(body.error || body.detail || t('admin.errorStatus', { status: r.status }));
        setData(null);
      }
    } catch (e) {
      setError(String(e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [service, lines, logDate, activeService.source, getAuthHeaders, t]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // Byter man service eller får ny lista med datum, välj senaste filen.
  useEffect(() => {
    if (activeService.source === 'mcp' && data?.available_dates?.length && !logDate) {
      setLogDate(data.available_dates[0]);
    }
    if (activeService.source !== 'mcp' && logDate) {
      setLogDate('');
    }
  }, [data, activeService.source, logDate]);

  useEffect(() => {
    if (preRef.current) {
      preRef.current.scrollTop = preRef.current.scrollHeight;
    }
  }, [data]);

  const sizeMb = data?.file_size_bytes != null
    ? (data.file_size_bytes / (1024 * 1024)).toFixed(1)
    : null;

  return (
    <div>
      {!hideTitle && <h1 className="admin-section-title">{t('admin.subLogs')}</h1>}

      <div className="admin-card">
        <div className="admin-logs-toolbar">
          <div className="admin-logs-tabs">
            {SERVICES.map((s) => (
              <button
                key={s.key}
                className={`admin-logs-tab ${service === s.key ? 'active' : ''}`}
                onClick={() => { setService(s.key); setLogDate(''); }}
              >
                {s.label}
              </button>
            ))}
          </div>

          <div className="admin-logs-controls">
            {activeService.source === 'mcp' && data?.available_dates?.length > 0 && (
              <label className="admin-logs-label">
                {t('admin.logDate')}&nbsp;
                <select
                  value={logDate || data.available_dates[0]}
                  onChange={(e) => setLogDate(e.target.value)}
                  className="admin-logs-select"
                >
                  {data.available_dates.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </label>
            )}
            <label className="admin-logs-label">
              {t('admin.logLines')}&nbsp;
              <select
                value={lines}
                onChange={(e) => setLines(Number(e.target.value))}
                className="admin-logs-select"
              >
                {LINE_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </label>
            <button className="admin-button" onClick={fetchLogs} disabled={loading}>
              {loading ? t('live.loading') : t('admin.refresh')}
            </button>
          </div>
        </div>

        {sizeMb !== null && (
          <div className="admin-info admin-logs-meta">
            {t('admin.logsMeta', { returned: data.returned, requested: data.requested, size: sizeMb })}
          </div>
        )}

        {error && <div className="admin-info admin-logs-error">{error}</div>}

        <pre ref={preRef} className="admin-logs-pre">
          {data?.lines?.length ? data.lines.join('\n') : (loading ? '' : t('admin.noLogs'))}
        </pre>
      </div>
    </div>
  );
}
